import { supabase } from './supabaseClient';
import {
  WorkflowTemplate,
  TemplateMatchingContext,
  ActionType,
  JourneyStage,
  JourneyMarket,
  JourneyPersona,
  SurveyTemplateContent,
  TaskTemplateContent,
  TeamTemplateContent,
  PreReleaseTemplateContent
} from '../types/journey';

/**
 * Service for generating comprehensive workflow templates for different business contexts
 * Covers idea validation, market research, MVP development, team formation, and launch campaigns
 */
export class WorkflowTemplateGenerationService {

  /**
   * Generates and stores all comprehensive templates for different business contexts
   */
  static async generateAllTemplates(): Promise<void> {
    const templates = [
      ...this.generateSurveyTemplates(),
      ...this.generateTaskTemplates(),
      ...this.generateTeamTemplates(),
      ...this.generatePreReleaseTemplates()
    ];

    // Insert templates in batches to avoid overwhelming the database
    const batchSize = 10;
    for (let i = 0; i < templates.length; i += batchSize) {
      const batch = templates.slice(i, i + batchSize);
      await this.insertTemplateBatch(batch);
    }
  }

  /**
   * Generates comprehensive survey templates for idea validation, market research, and user feedback
   */
  private static generateSurveyTemplates(): Omit<WorkflowTemplate, 'id' | 'createdAt' | 'updatedAt'>[] {
    return [
      // Idea Validation Surveys
      {
        type: 'survey',
        title: 'E-commerce Idea Validation Survey',
        conditions: ['stage == "Beginner"', 'market == "E-commerce"'],
        content: {
          title: 'Validate Your E-commerce Business Idea',
          description: 'Get feedback on your e-commerce concept from potential customers',
          questions: [
            'What frustrates you most about online shopping?',
            'How do you currently solve this problem?',
            'Would you be interested in a product/service that [describe your solution]?',
            'What would make you choose this over existing alternatives?',
            'How much would you pay for this solution?',
            'How often would you use this product/service?',
            'What features are most important to you?',
            'What would prevent you from buying this?'
          ],
          targetResponses: 25
        } as SurveyTemplateContent,
        isActive: true
      },
      {
        type: 'survey',
        title: 'Tech Solution Idea Validation',
        conditions: ['stage == "Beginner"', 'market == "IT/Tech"'],
        content: {
          title: 'Validate Your Technical Solution',
          description: 'Research demand for your technical product or service',
          questions: [
            'What technical challenges do you face daily in your work?',
            'How do you currently handle these challenges?',
            'What tools do you use and what are their limitations?',
            'Would a solution that [describe your idea] be valuable to you?',
            'How much time would this save you per week?',
            'What would you pay for a tool that solves this problem?',
            'What features would be essential vs nice-to-have?',
            'How do you typically evaluate and adopt new technical tools?'
          ],
          targetResponses: 20
        } as SurveyTemplateContent,
        isActive: true
      },
      {
        type: 'survey',
        title: 'Social Media Business Idea Validation',
        conditions: ['stage == "Beginner"', 'market == "Social Media/Influencer"'],
        content: {
          title: 'Validate Your Social Media Business Concept',
          description: 'Test your social media or influencer business idea',
          questions: [
            'What challenges do you face as a content creator or influencer?',
            'What tools or services do you wish existed in this space?',
            'Would you pay for a solution that [describe your idea]?',
            'How much do you currently spend on similar tools/services?',
            'What would make you switch from your current solution?',
            'How important is community/networking in your decision?',
            'What pricing model would you prefer?',
            'What would convince you to try a new platform/service?'
          ],
          targetResponses: 30
        } as SurveyTemplateContent,
        isActive: true
      },

      // Market Research Surveys
      {
        type: 'survey',
        title: 'E-commerce Market Research Survey',
        conditions: ['stage == "Have Idea"', 'market == "E-commerce"'],
        content: {
          title: 'E-commerce Market Research',
          description: 'Deep dive into your target market and customer behavior',
          questions: [
            'How often do you shop online?',
            'What categories do you buy online vs in-store?',
            'What influences your online purchasing decisions?',
            'Which e-commerce platforms do you use most?',
            'What makes you abandon a shopping cart?',
            'How important are reviews and ratings to you?',
            'What shipping options do you prefer?',
            'How do you discover new products online?',
            'What would improve your online shopping experience?',
            'What are your biggest concerns about online shopping?'
          ],
          targetResponses: 50
        } as SurveyTemplateContent,
        isActive: true
      },
      {
        type: 'survey',
        title: 'Tech Market Competitive Analysis',
        conditions: ['stage == "Have Idea"', 'market == "IT/Tech"'],
        content: {
          title: 'Technical Market Competitive Research',
          description: 'Understand your competitive landscape and market positioning',
          questions: [
            'What tools do you currently use for [your solution category]?',
            'How satisfied are you with your current tools (1-10)?',
            'What features do you wish your current tools had?',
            'How much do you spend monthly on these tools?',
            'What would make you switch to a new solution?',
            'How do you typically discover new technical tools?',
            'What factors are most important in your tool selection?',
            'Have you tried alternatives? What made you switch back?',
            'What integration capabilities are essential?',
            'How important is customer support vs self-service?'
          ],
          targetResponses: 30
        } as SurveyTemplateContent,
        isActive: true
      },

      // User Feedback Surveys
      {
        type: 'survey',
        title: 'Product User Feedback Survey',
        conditions: ['stage == "In Progress"'],
        content: {
          title: 'User Experience Feedback',
          title_zh: '用户体验反馈',
          description: 'Collect feedback from users who have tried your product',
          description_zh: '收集试用过您产品的用户的反馈',
          questions: [
            {
              text: 'How did you first hear about our product?',
              text_zh: '您最初是如何了解我们的产品的？',
              type: 'multiple-choice',
              options: ['Search engine', 'Social media', 'Friend recommendation', 'Online ad', 'Blog/article', 'Other'],
              options_zh: ['搜索引擎', '社交媒体', '朋友推荐', '在线广告', '博客/文章', '其他']
            },
            {
              text: 'What problem were you trying to solve when you found us?',
              text_zh: '当您找到我们时，您试图解决什么问题？',
              type: 'text',
              options: [],
              options_zh: []
            },
            {
              text: 'How well does our product solve your problem (1-10)?',
              text_zh: '我们的产品解决您的问题的效果如何（1-10分）？',
              type: 'multiple-choice',
              options: ['1-2 (Not at all)', '3-4 (Poorly)', '5-6 (Somewhat)', '7-8 (Well)', '9-10 (Perfectly)'],
              options_zh: ['1-2（完全没有）', '3-4（很差）', '5-6（一般）', '7-8（很好）', '9-10（完美）']
            },
            {
              text: 'What do you like most about our product?',
              text_zh: '您最喜欢我们产品的哪些方面？',
              type: 'text',
              options: [],
              options_zh: []
            },
            {
              text: 'What frustrates you most about our product?',
              text_zh: '我们产品最让您感到沮丧的是什么？',
              type: 'text',
              options: [],
              options_zh: []
            },
            {
              text: 'What features are missing that you need?',
              text_zh: '您需要哪些缺失的功能？',
              type: 'text',
              options: [],
              options_zh: []
            },
            {
              text: 'How likely are you to recommend us to others (1-10)?',
              text_zh: '您向他人推荐我们的可能性有多大（1-10分）？',
              type: 'multiple-choice',
              options: ['1-2 (Not likely)', '3-4 (Unlikely)', '5-6 (Neutral)', '7-8 (Likely)', '9-10 (Very likely)'],
              options_zh: ['1-2（不太可能）', '3-4（不可能）', '5-6（中立）', '7-8（可能）', '9-10（非常可能）']
            },
            {
              text: 'What would make you use our product more often?',
              text_zh: '什么会让您更频繁地使用我们的产品？',
              type: 'text',
              options: [],
              options_zh: []
            },
            {
              text: 'How does our product compare to alternatives?',
              text_zh: '我们的产品与替代品相比如何？',
              type: 'multiple-choice',
              options: ['Much better', 'Somewhat better', 'About the same', 'Somewhat worse', 'Much worse', 'No alternatives tried'],
              options_zh: ['好得多', '稍好一些', '差不多', '稍差一些', '差得多', '没有尝试过替代品']
            },
            {
              text: 'What would you improve about the user experience?',
              text_zh: '您会如何改进用户体验？',
              type: 'text',
              options: [],
              options_zh: []
            }
          ],
          targetResponses: 40
        } as SurveyTemplateContent,
        isActive: true
      },

      // Influencer-specific surveys
      {
        type: 'survey',
        title: 'Audience Business Opportunity Poll',
        conditions: ['persona == "Influencer"'],
        content: {
          title: 'What Business Should I Start?',
          description: 'Poll your audience about business opportunities they want from you',
          questions: [
            'What type of product/service would you want from me?',
            'What problems can I help you solve?',
            'Would you pay for premium content or services from me?',
            'What format would you prefer? (course, consulting, product, community)',
            'What price range seems fair for this value?',
            'What topics would you want me to cover?',
            'How would you prefer to access premium content?',
            'What would make you join a paid community I create?'
          ],
          targetResponses: 100
        } as SurveyTemplateContent,
        isActive: true
      }
    ];
  }

  /**
   * Generates comprehensive task templates for MVP development, content creation, and user testing
   */
  private static generateTaskTemplates(): Omit<WorkflowTemplate, 'id' | 'createdAt' | 'updatedAt'>[] {
    return [
      // MVP Development Tasks
      {
        type: 'task',
        title: 'Build a 1-Week MVP',
        conditions: ['stage == "Have Idea"', 'market == "E-commerce"'],
        content: {
          title: 'Build a 1-Week MVP',
          description: 'Create a live, ultra-simple version of the tool',
          checklist: [
            'Define the ONE core action: "My tool will [input] -> [output]." (e.g., "Take a blog post URL -> Return a social media post.")',
            'Choose your tool: No-code (Bubble, Softr) or Code + AI (to build faster)',
            'Build only the core feature. Ignore logins, pretty design, and settings',
            'Deploy it online so people can use it',
            'Make a simple landing page with a GIF of the tool working and an email signup'
          ],
          expectedOutputs: [
            'Completed a live ultra simple version of the solution'
          ],
          timeEstimate: '1 week'
        } as TaskTemplateContent,
        isActive: true
      },
      {
        type: 'task',
        title: 'Build a 1-Week MVP',
        conditions: ['stage == "Have Idea"'],
        content: {
          title: 'Build a 1-Week MVP',
          description: 'Create a live, ultra-simple version of the tool',
          checklist: [
            'Define the ONE core action: "My tool will [input] -> [output]." (e.g., "Take a blog post URL -> Return a social media post.")',
            'Choose your tool: No-code (Bubble, Softr) or Code + AI (to build faster)',
            'Build only the core feature. Ignore logins, pretty design, and settings',
            'Deploy it online so people can use it',
            'Make a simple landing page with a GIF of the tool working and an email signup'
          ],
          expectedOutputs: [
            'Completed a live ultra simple version of the solution'
          ],
          timeEstimate: '1 week'
        } as TaskTemplateContent,
        isActive: true
      },
      {
        type: 'task',
        title: 'Build a 1-Week MVP',
        conditions: ['stage == "Have Idea"', 'market == "Other"'],
        content: {
          title: 'Build a 1-Week MVP',
          description: 'Create a live, ultra-simple version of the tool',
          checklist: [
            'Define the ONE core action: "My tool will [input] -> [output]." (e.g., "Take a blog post URL -> Return a social media post.")',
            'Choose your tool: No-code (Bubble, Softr) or Code + AI (to build faster)',
            'Build only the core feature. Ignore logins, pretty design, and settings',
            'Deploy it online so people can use it',
            'Make a simple landing page with a GIF of the tool working and an email signup'
          ],
          expectedOutputs: [
            'Completed a live ultra simple version of the solution'
          ],
          timeEstimate: '1 week'
        } as TaskTemplateContent,
        isActive: true
      },

      // Content Creation Tasks
      {
        type: 'task',
        title: 'Build a 1-Week MVP',
        conditions: ['persona == "Influencer"'],
        content: {
          title: 'Build a 1-Week MVP',
          description: 'Create a live, ultra-simple version of the tool',
          checklist: [
            'Define the ONE core action: "My tool will [input] -> [output]." (e.g., "Take a blog post URL -> Return a social media post.")',
            'Choose your tool: No-code (Bubble, Softr) or Code + AI (to build faster)',
            'Build only the core feature. Ignore logins, pretty design, and settings',
            'Deploy it online so people can use it',
            'Make a simple landing page with a GIF of the tool working and an email signup'
          ],
          expectedOutputs: [
            'Completed a live ultra simple version of the solution'
          ],
          timeEstimate: '1 week'
        } as TaskTemplateContent,
        isActive: true
      },
      {
        type: 'task',
        title: 'Digital Marketing Foundation',
        conditions: ['stage == "In Progress"'],
        content: {
          title: 'Build Your Digital Marketing Foundation',
          description: 'Set up essential marketing channels and content systems',
          checklist: [
            'Set up Google My Business and local listings',
            'Create social media profiles on relevant platforms',
            'Set up email marketing system (Mailchimp, ConvertKit)',
            'Create lead magnets and opt-in forms',
            'Set up basic SEO (Google Search Console, keywords)',
            'Create content calendar and posting schedule',
            'Set up social media management tools',
            'Create brand guidelines and visual assets',
            'Set up Google Analytics and conversion tracking',
            'Create customer testimonial collection system',
            'Set up basic paid advertising accounts',
            'Create referral program or system'
          ],
          expectedOutputs: [
            'Active social media presence',
            'Email marketing system with subscribers',
            'SEO foundation and keyword strategy',
            'Content calendar for 30 days',
            'Analytics and tracking setup'
          ],
          timeEstimate: '2-3 weeks'
        } as TaskTemplateContent,
        isActive: true
      },

      // User Testing Tasks
      {
        type: 'task',
        title: 'User Testing and Feedback Collection',
        conditions: ['stage == "In Progress"'],
        content: {
          title: 'Conduct User Testing and Collect Feedback',
          description: 'Test your product with real users and gather actionable feedback',
          checklist: [
            'Recruit 10-15 target users for testing',
            'Create user testing scenarios and tasks',
            'Set up user testing tools (screen recording, surveys)',
            'Conduct moderated user testing sessions',
            'Run unmoderated testing with larger group',
            'Collect quantitative data (completion rates, time on task)',
            'Gather qualitative feedback through interviews',
            'Analyze user behavior and pain points',
            'Create prioritized list of improvements',
            'Test key user flows and conversion funnels',
            'Document findings and recommendations',
            'Plan next iteration based on feedback'
          ],
          expectedOutputs: [
            'User testing results from 10+ users',
            'Prioritized improvement recommendations',
            'User behavior analytics and insights',
            'Updated product roadmap',
            'Improved user experience based on feedback'
          ],
          timeEstimate: '2-3 weeks'
        } as TaskTemplateContent,
        isActive: true
      }
    ];
  }

  /**
   * Generates team role templates based on skill gaps and business needs
   */
  private static generateTeamTemplates(): Omit<WorkflowTemplate, 'id' | 'createdAt' | 'updatedAt'>[] {
    return [
      // E-commerce team templates
      {
        type: 'team',
        title: 'E-commerce Startup Team',
        conditions: ['market == "E-commerce"', 'persona == "Builder with Gaps"'],
        content: {
          title: 'Build Your E-commerce Team',
          description: 'Find team members to complement your e-commerce business skills',
          roles: [
            {
              title: 'Digital Marketing Specialist',
              description: 'Drive customer acquisition through digital channels and social media',
              skills: ['Social Media Marketing', 'Google Ads', 'Facebook Ads', 'Content Creation', 'Email Marketing', 'SEO'],
              timeCommitment: '15-20 hours/week'
            },
            {
              title: 'Customer Success Manager',
              description: 'Handle customer service, support, and retention',
              skills: ['Customer Service', 'Communication', 'Problem Solving', 'CRM Management', 'Customer Retention'],
              timeCommitment: '10-15 hours/week'
            },
            {
              title: 'Operations and Fulfillment Manager',
              description: 'Manage inventory, shipping, suppliers, and day-to-day operations',
              skills: ['Operations Management', 'Logistics', 'Inventory Management', 'Supplier Relations', 'Process Optimization'],
              timeCommitment: '20-25 hours/week'
            },
            {
              title: 'Product Manager',
              description: 'Manage product catalog, sourcing, and product development',
              skills: ['Product Management', 'Sourcing', 'Vendor Relations', 'Market Research', 'Product Development'],
              timeCommitment: '15-20 hours/week'
            }
          ]
        } as TeamTemplateContent,
        isActive: true
      },
      {
        type: 'team',
        title: 'Tech Startup Core Team',
        conditions: ['market == "IT/Tech"', 'persona == "Builder with Gaps"'],
        content: {
          title: 'Build Your Tech Startup Team',
          description: 'Assemble a technical team for your software product',
          roles: [
            {
              title: 'Frontend Developer',
              description: 'Build user interfaces and optimize user experience',
              skills: ['React', 'JavaScript', 'TypeScript', 'CSS', 'UI/UX Design', 'Mobile Development'],
              timeCommitment: '25-30 hours/week'
            },
            {
              title: 'Backend Developer',
              description: 'Handle server-side logic, databases, APIs, and infrastructure',
              skills: ['Node.js', 'Python', 'Database Design', 'API Development', 'Cloud Services', 'DevOps'],
              timeCommitment: '25-30 hours/week'
            },
            {
              title: 'Product Manager',
              description: 'Define features, manage roadmap, and coordinate development',
              skills: ['Product Management', 'User Research', 'Project Management', 'Analytics', 'Market Research'],
              timeCommitment: '20-25 hours/week'
            },
            {
              title: 'DevOps Engineer',
              description: 'Manage deployment, monitoring, and infrastructure',
              skills: ['AWS/Azure', 'Docker', 'CI/CD', 'Monitoring', 'Security', 'Infrastructure as Code'],
              timeCommitment: '15-20 hours/week'
            }
          ]
        } as TeamTemplateContent,
        isActive: true
      },
      {
        type: 'team',
        title: 'Content Creator Business Team',
        conditions: ['market == "Social Media/Influencer"', 'persona == "Influencer"'],
        content: {
          title: 'Scale Your Content Business',
          description: 'Build a team to help scale your content and influence business',
          roles: [
            {
              title: 'Content Manager',
              description: 'Help create, edit, and manage your content across platforms',
              skills: ['Content Creation', 'Video Editing', 'Social Media Management', 'Copywriting', 'Brand Management'],
              timeCommitment: '20-25 hours/week'
            },
            {
              title: 'Community Manager',
              description: 'Engage with your audience and manage community interactions',
              skills: ['Community Management', 'Customer Service', 'Social Media', 'Communication', 'Engagement Strategy'],
              timeCommitment: '15-20 hours/week'
            },
            {
              title: 'Business Development Manager',
              description: 'Handle partnerships, sponsorships, and business opportunities',
              skills: ['Business Development', 'Partnership Management', 'Negotiation', 'Sales', 'Relationship Building'],
              timeCommitment: '10-15 hours/week'
            },
            {
              title: 'Virtual Assistant',
              description: 'Handle administrative tasks, scheduling, and general support',
              skills: ['Administrative Support', 'Scheduling', 'Email Management', 'Research', 'Organization'],
              timeCommitment: '10-15 hours/week'
            }
          ]
        } as TeamTemplateContent,
        isActive: true
      },

      // General business team templates
      {
        type: 'team',
        title: 'Service Business Team',
        conditions: ['market == "Other"', 'persona == "Builder with Gaps"'],
        content: {
          title: 'Build Your Service Business Team',
          description: 'Find team members to help scale your service-based business',
          roles: [
            {
              title: 'Sales Representative',
              description: 'Generate leads and close new business opportunities',
              skills: ['Sales', 'Lead Generation', 'CRM Management', 'Communication', 'Negotiation'],
              timeCommitment: '20-25 hours/week'
            },
            {
              title: 'Project Manager',
              description: 'Manage client projects and ensure timely delivery',
              skills: ['Project Management', 'Client Communication', 'Process Management', 'Quality Control'],
              timeCommitment: '15-20 hours/week'
            },
            {
              title: 'Marketing Coordinator',
              description: 'Handle marketing campaigns and lead generation',
              skills: ['Digital Marketing', 'Content Marketing', 'Lead Generation', 'Social Media', 'Email Marketing'],
              timeCommitment: '15-20 hours/week'
            },
            {
              title: 'Administrative Assistant',
              description: 'Handle scheduling, invoicing, and administrative tasks',
              skills: ['Administrative Support', 'Bookkeeping', 'Scheduling', 'Customer Service', 'Organization'],
              timeCommitment: '10-15 hours/week'
            }
          ]
        } as TeamTemplateContent,
        isActive: true
      },

      // Skill-specific team templates
      {
        type: 'team',
        title: 'Technical Skills Support Team',
        conditions: ['persona == "Builder with Gaps"'],
        content: {
          title: 'Get Technical Skills Support',
          description: 'Find team members to handle technical aspects you need help with',
          roles: [
            {
              title: 'Web Developer',
              description: 'Build and maintain your website and web applications',
              skills: ['Web Development', 'HTML/CSS', 'JavaScript', 'WordPress', 'E-commerce Platforms'],
              timeCommitment: '10-20 hours/week'
            },
            {
              title: 'Graphic Designer',
              description: 'Create visual assets, branding, and marketing materials',
              skills: ['Graphic Design', 'Branding', 'Adobe Creative Suite', 'Web Design', 'Print Design'],
              timeCommitment: '5-15 hours/week'
            },
            {
              title: 'Data Analyst',
              description: 'Analyze business data and provide insights for decision making',
              skills: ['Data Analysis', 'Google Analytics', 'Excel', 'Reporting', 'Business Intelligence'],
              timeCommitment: '5-10 hours/week'
            },
            {
              title: 'SEO Specialist',
              description: 'Improve your online visibility and search engine rankings',
              skills: ['SEO', 'Keyword Research', 'Content Optimization', 'Link Building', 'Technical SEO'],
              timeCommitment: '5-10 hours/week'
            }
          ]
        } as TeamTemplateContent,
        isActive: true
      }
    ];
  }

  /**
   * Generates pre-release project templates for landing pages, beta testing, and launch campaigns
   */
  private static generatePreReleaseTemplates(): Omit<WorkflowTemplate, 'id' | 'createdAt' | 'updatedAt'>[] {
    return [
      // AI-Enhanced Pre-Release Templates
      {
        type: 'preRelease',
        title: 'AI-Enhanced Pre-Release Project for Beginners',
        conditions: ['stage == "Beginner"', 'persona == "skilled-maker"'],
        content: {
          title: 'AI-Generated Pre-Release Project',
          description: 'Generate a contextual pre-release project based on your selected business idea using AI',
          headline: 'Test Your Idea with Real Users - AI-Powered Pre-Release',
          features: [
            'AI-generated project tailored to your specific business idea',
            'Contextual landing page copy and messaging',
            'Targeted beta program assets and email templates',
            'Validation survey questions specific to your solution',
            'Social media content optimized for your market'
          ],
          targetSignups: 50,
          launchGoals: [
            'Generate AI-powered pre-release project based on selected idea',
            'Deploy contextual landing page and beta program',
            'Collect 20+ qualified beta user signups',
            'Validate core assumptions through user feedback',
            'Measure real demand and willingness to pay'
          ],
          aiEnhanced: true,
          useSelectedIdea: true
        } as PreReleaseTemplateContent & { aiEnhanced: boolean; useSelectedIdea: boolean },
        isActive: true
      },
      // Landing page templates
      {
        type: 'preRelease',
        title: 'E-commerce Product Launch Landing Page',
        conditions: ['market == "E-commerce"'],
        content: {
          title: 'Pre-Launch Your E-commerce Product',
          description: 'Build anticipation and collect early customers before your e-commerce launch',
          headline: 'Revolutionary [Product Category] Coming Soon - Get Early Access',
          features: [
            'Solves the #1 problem in [your market] that customers complain about',
            'Premium quality at an affordable price point',
            'Easy to use with intuitive design',
            'Fast shipping and excellent customer service',
            'Money-back guarantee for complete satisfaction'
          ],
          targetSignups: 200,
          launchGoals: [
            'Collect 200+ email signups from target customers',
            'Validate pricing through early bird offers',
            'Build social media buzz and word-of-mouth',
            'Identify brand advocates and potential reviewers',
            'Test messaging and value proposition'
          ]
        } as PreReleaseTemplateContent,
        isActive: true
      },
      {
        type: 'preRelease',
        title: 'Tech Product Beta Testing Program',
        conditions: ['market == "IT/Tech"'],
        content: {
          title: 'Beta Test Your Technical Solution',
          description: 'Recruit technical users to test your product and provide feedback',
          headline: 'Be Among the First to Try [Your Solution] - Join Our Beta',
          features: [
            'Cutting-edge technology that solves real problems',
            'Built by developers, for developers',
            'Free access during beta period',
            'Direct input on product development',
            'Early access to new features and updates'
          ],
          targetSignups: 100,
          launchGoals: [
            'Recruit 100+ qualified beta testers',
            'Collect detailed usage feedback and bug reports',
            'Validate product-market fit with real users',
            'Build community of early adopters',
            'Generate testimonials and case studies'
          ]
        } as PreReleaseTemplateContent,
        isActive: true
      },
      {
        type: 'preRelease',
        title: 'Influencer Product Launch to Audience',
        conditions: ['persona == "Influencer"'],
        content: {
          title: 'Launch Your Product to Your Audience',
          description: 'Leverage your existing audience for an exclusive product launch',
          headline: 'Exclusive for My Community: [Your Product/Service] is Here',
          features: [
            'Created based on your feedback and requests',
            'Exclusive access for my community members',
            'Special launch pricing just for you',
            'Direct access to me for questions and support',
            'Community of like-minded people using the same solution'
          ],
          targetSignups: 500,
          launchGoals: [
            'Convert 15-20% of audience to signups',
            'Generate authentic testimonials from followers',
            'Create viral sharing and word-of-mouth',
            'Validate premium pricing with engaged audience',
            'Build foundation for ongoing product sales'
          ]
        } as PreReleaseTemplateContent,
        isActive: true
      },

      // Beta testing templates
      {
        type: 'preRelease',
        title: 'Service Business Beta Testing',
        conditions: ['market == "Other"', 'stage == "Have Idea"'],
        content: {
          title: 'Beta Test Your Service Offering',
          description: 'Test your service with a small group of clients before full launch',
          headline: 'Be Our Beta Client - Get Premium Service at Beta Pricing',
          features: [
            'Personalized service tailored to your needs',
            'Beta pricing - 50% off regular rates',
            'Direct access to the founder',
            'Your feedback shapes our service offering',
            'Priority support and attention'
          ],
          targetSignups: 25,
          launchGoals: [
            'Work with 10-15 beta clients',
            'Refine service delivery process',
            'Collect testimonials and case studies',
            'Validate pricing and service packages',
            'Build referral network from satisfied beta clients'
          ]
        } as PreReleaseTemplateContent,
        isActive: true
      },

      // Launch campaign templates
      {
        type: 'preRelease',
        title: 'Product Hunt Launch Campaign',
        conditions: ['market == "IT/Tech"', 'stage == "In Progress"'],
        content: {
          title: 'Product Hunt Launch Campaign',
          description: 'Prepare for a successful Product Hunt launch to gain visibility',
          headline: 'We\'re Launching on Product Hunt - Support Us!',
          features: [
            'Innovative solution to a common problem',
            'Built with user feedback and real needs in mind',
            'Free tier available for everyone to try',
            'Growing community of satisfied users',
            'Continuous improvement based on user feedback'
          ],
          targetSignups: 1000,
          launchGoals: [
            'Get 500+ upvotes on Product Hunt',
            'Generate 1000+ website visits on launch day',
            'Collect 200+ new email subscribers',
            'Get featured in tech newsletters and blogs',
            'Build momentum for ongoing growth'
          ]
        } as PreReleaseTemplateContent,
        isActive: true
      },
      {
        type: 'preRelease',
        title: 'Social Media Launch Campaign',
        conditions: ['market == "Social Media/Influencer"'],
        content: {
          title: 'Social Media Product Launch',
          description: 'Create buzz and drive signups through social media campaign',
          headline: 'The Tool Every Content Creator Needs - Now Available',
          features: [
            'Designed specifically for content creators and influencers',
            'Saves hours of work every week',
            'Used by successful creators you follow',
            'Affordable pricing for creators at any level',
            'Community of creators sharing tips and strategies'
          ],
          targetSignups: 750,
          launchGoals: [
            'Generate 10,000+ social media impressions',
            'Get 100+ shares and reposts from users',
            'Collect 750+ email signups',
            'Partner with 5+ influencers for promotion',
            'Create viral moment around launch'
          ]
        } as PreReleaseTemplateContent,
        isActive: true
      },

      // General launch templates
      {
        type: 'preRelease',
        title: 'Soft Launch Campaign',
        conditions: ['stage == "In Progress"'],
        content: {
          title: 'Soft Launch Your Product',
          description: 'Test your launch process with a limited audience before going wide',
          headline: 'Limited Early Access - Join Our Soft Launch',
          features: [
            'Be among the first to experience our solution',
            'Special early adopter pricing and benefits',
            'Direct feedback channel to the founding team',
            'Help shape the future of the product',
            'Exclusive access to new features as they launch'
          ],
          targetSignups: 150,
          launchGoals: [
            'Test all launch processes and systems',
            'Collect feedback on user experience',
            'Validate pricing and positioning',
            'Build initial customer base',
            'Prepare for full public launch'
          ]
        } as PreReleaseTemplateContent,
        isActive: true
      }
    ];
  }

  /**
   * Inserts a batch of templates into the database
   */
  private static async insertTemplateBatch(
    templates: Omit<WorkflowTemplate, 'id' | 'createdAt' | 'updatedAt'>[]
  ): Promise<void> {
    const { error } = await supabase
      .from('workflow_templates')
      .insert(templates.map(template => ({
        type: template.type,
        title: template.title,
        conditions: template.conditions,
        content: template.content,
        is_active: template.isActive
      })));

    if (error) {
      throw new Error(`Failed to insert template batch: ${error.message}`);
    }
  }

  /**
   * Gets templates by business context (stage, market, persona combination)
   */
  static async getTemplatesByContext(context: TemplateMatchingContext): Promise<WorkflowTemplate[]> {
    const { data, error } = await supabase
      .from('workflow_templates')
      .select('*')
      .eq('is_active', true);

    if (error) {
      throw new Error(`Failed to fetch templates: ${error.message}`);
    }

    // Filter templates that match the context
    return data
      .map(row => ({
        id: row.id,
        type: row.type,
        title: row.title,
        conditions: row.conditions,
        content: row.content,
        isActive: row.is_active,
        createdAt: new Date(row.created_at),
        updatedAt: new Date(row.updated_at)
      }))
      .filter(template => this.evaluateTemplateConditions(template, context));
  }

  /**
   * Evaluates if a template matches the given context
   */
  private static evaluateTemplateConditions(
    template: WorkflowTemplate,
    context: TemplateMatchingContext
  ): boolean {
    return template.conditions.every(condition => {
      const match = condition.match(/^(\w+)\s*(==|!=)\s*["']([^"']+)["']$/);
      if (!match) return false;

      const [, field, operator, value] = match;
      const contextValue = (context as any)[field];

      if (operator === '==') {
        return contextValue === value;
      } else if (operator === '!=') {
        return contextValue !== value;
      }

      return false;
    });
  }

  /**
   * Gets template statistics for monitoring and analytics
   */
  static async getTemplateStatistics(): Promise<{
    totalTemplates: number;
    templatesByType: Record<ActionType, number>;
    templatesByMarket: Record<JourneyMarket, number>;
    templatesByStage: Record<JourneyStage, number>;
    templatesByPersona: Record<JourneyPersona, number>;
  }> {
    const { data, error } = await supabase
      .from('workflow_templates')
      .select('type, conditions')
      .eq('is_active', true);

    if (error) {
      throw new Error(`Failed to fetch template statistics: ${error.message}`);
    }

    const stats = {
      totalTemplates: data.length,
      templatesByType: {} as Record<ActionType, number>,
      templatesByMarket: {} as Record<JourneyMarket, number>,
      templatesByStage: {} as Record<JourneyStage, number>,
      templatesByPersona: {} as Record<JourneyPersona, number>
    };

    // Count templates by type
    data.forEach(template => {
      stats.templatesByType[template.type as ActionType] =
        (stats.templatesByType[template.type as ActionType] || 0) + 1;

      // Parse conditions to count by market, stage, persona
      template.conditions.forEach((condition: string) => {
        const match = condition.match(/^(\w+)\s*==\s*["']([^"']+)["']$/);
        if (match) {
          const [, field, value] = match;
          if (field === 'market') {
            stats.templatesByMarket[value as JourneyMarket] =
              (stats.templatesByMarket[value as JourneyMarket] || 0) + 1;
          } else if (field === 'stage') {
            stats.templatesByStage[value as JourneyStage] =
              (stats.templatesByStage[value as JourneyStage] || 0) + 1;
          } else if (field === 'persona') {
            stats.templatesByPersona[value as JourneyPersona] =
              (stats.templatesByPersona[value as JourneyPersona] || 0) + 1;
          }
        }
      });
    });

    return stats;
  }
}